import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Logger } from "../../infra/logger/logger";
import { PurchaseOrderInputFilter } from "domain/models/purchase-order-input-filter";
import purchaseOrderRepository from "infra/repositories/purchase-order-repository";
import { PurchaseOrder } from "domain/entities/purchase-order";
import { httpClient } from "infra/axios/axios-api";

export const usePurchaseOrdersByFilter = (filter: PurchaseOrderInputFilter) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["purchaseOrders", filter],
    queryFn: async () => {
      try {
        const result = await purchaseOrderRepository.getAll(filter);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_DEFAULT) || 0,
  });
};

export const usePurchaseOrdersById = (id?: string) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["purchaseOrder", id],
    queryFn: async () => {
      try {
        if (!id) throw new Error("PurchaseOrder ID inválido");
        const result = await purchaseOrderRepository.getById(id);

        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      }
    },
    enabled: !!id,
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
  });
};

export const useCreatePurchaseOrderMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: PurchaseOrder) => {
      try {
        const result = await purchaseOrderRepository.create(data);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError && error.response?.data?.metadata?.errors) {
          const errors = error.response.data.metadata.errors;
          const customError = new Error("Erro na criação da purchaseOrder");
          (customError as any).errors = errors;
          throw customError;
        }
        throw error;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["purchaseOrders", {}] });
    },
    onError: (error: any) => {
      logger.error(error.message, error);
    },
  });
};

export const useUpdatePurchaseOrderMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: PurchaseOrder) => {
      try {
        const result = await purchaseOrderRepository.update(data);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError && error.response?.data?.metadata?.errors) {
          const errors = error.response.data.metadata.errors;
          const customError = new Error("Erro na atualização da purchaseOrder");
          (customError as any).errors = errors;
          throw customError;
        }
        throw error;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["purchaseOrders", {}] });
    },
    onError: (error: any) => {
      logger.error(error.message, error);
    },
  });
};

export const useDeletePurchaseOrderMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: PurchaseOrder) => {
      if (!data || !data.id) throw new Error("PurchaseOrder ID inválido");
      try {
        const result = await purchaseOrderRepository.delete(data.id);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError && error.response?.data?.metadata?.errors) {
          const errors = error.response.data.metadata.errors;
          const customError = new Error("Erro na eliminação da purchaseOrder");
          (customError as any).errors = errors;
          throw customError;
        }
        throw error;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["purchaseOrders", {}] });
    },
    onError: (error: any) => {
      logger.error(error.message, error);
    },
  });
};

export const usePurchaseOrderUpdateStatusMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status, justification }: { id: string, status: string, justification: string }) => {
      try {
        const result = await purchaseOrderRepository.updateStatus(id, status, justification);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError && error.response?.data?.metadata?.errors) {
          const errors = error.response.data.metadata.errors;
          const customError = new Error("Erro na atualização do status");
          (customError as any).errors = errors;
          throw customError;
        }
        throw error;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["purchaseOrders"] });
    },
    onError: (error) => {
      logger.error("Erro ao alterar o status da PurchaseOrder:", error);
    },
  });
};

export const usePurchaseOrderSummary = (month: number, year: number) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["purchaseOrderSummary", month, year],
    queryFn: async () => {
      try {
        const response = await httpClient.get(`/purchaseOrder/summary/${month}/${year}`);
        return response.data;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata?.errors?.[0] || error.message,
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata?.errors?.[0] || "Ocorreu um erro ao buscar o resumo");
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_DEFAULT) || 0,
  });
};

export const usePurchaseOrderConsumptionPreview = (id?: string) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["purchaseOrderConsumptionPreview", id],
    queryFn: async () => {
      try {
        if (!id) return null;
        const response = await httpClient.get(`/purchaseOrder/${id}/consumption-preview`);
        return response.data;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata?.errors?.[0] || error.message,
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata?.errors?.[0] || "Ocorreu um erro ao calcular o consumo");
        }
        throw error;
      }
    },
    enabled: !!id,
    retry: 2,
    staleTime: 0,
  });
};
